import React, { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { Table, Space, Select, Input, Button, Row, Col, Card, Empty, Modal } from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Sandbox, SandboxStatus } from '../../types/sandbox';
import { StatusBadge } from '../../components/sandboxes/StatusBadge';
import { URLDisplay } from '../../components/sandboxes/URLDisplay';
import { useDelete } from '@refinedev/core';

const { Search } = Input;
const { Option } = Select;

export const SandboxList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<SandboxStatus | undefined>();
  const [clusterFilter, setClusterFilter] = useState<string | undefined>();
  const [searchText, setSearchText] = useState<string>('');

  const { tableProps, filters, setFilters, tableQueryResult } =
    useTable<Sandbox>({
      resource: 'sandboxes',
      sorters: {
        initial: [
          {
            field: 'created_at',
            order: 'desc',
          },
        ],
      },
      filters: {
        permanent: [],
      },
      syncWithLocation: false,
    });

  const { mutate: deleteSandbox } = useDelete();

  // Handle refresh
  const handleRefresh = () => {
    tableQueryResult.refetch();
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: SandboxStatus | undefined) => {
    setStatusFilter(value);
    const newFilters = filters?.filter((f) => !('field' in f && f.field === 'status')) || [];
    if (value) {
      newFilters.push({
        field: 'status',
        operator: 'eq',
        value: value,
      });
    }
    setFilters(newFilters);
  };

  // Handle cluster filter change
  const handleClusterFilterChange = (value: string | undefined) => {
    setClusterFilter(value);
    const newFilters = filters?.filter((f) => !('field' in f && f.field === 'cluster')) || [];
    if (value) {
      newFilters.push({
        field: 'cluster',
        operator: 'eq',
        value: value,
      });
    }
    setFilters(newFilters);
  };

  // Handle delete with confirmation
  const handleDelete = (record: Sandbox) => {
    Modal.confirm({
      title: 'Delete Sandbox',
      content: `Are you sure you want to delete sandbox "${record.id}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteSandbox({
          resource: 'sandboxes',
          id: record.id,
        });
      },
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStatusFilter(undefined);
    setClusterFilter(undefined);
    setSearchText('');
    setFilters([]);
  };

  const hasActiveFilters = statusFilter || clusterFilter || searchText;

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
        </>
      )}
    >
      {/* Filter Section */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder="Filter by Status"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <Option value="pending">Pending</Option>
              <Option value="creating">Creating</Option>
              <Option value="ready">Ready</Option>
              <Option value="failed">Failed</Option>
              <Option value="deleting">Deleting</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder="Filter by Cluster"
              style={{ width: '100%' }}
              value={clusterFilter}
              onChange={handleClusterFilterChange}
            >
              <Option value="staging-cluster">Staging</Option>
              <Option value="production-cluster">Production</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by Sandbox ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            {hasActiveFilters && (
              <Button block onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Table
        {...tableProps}
        rowKey="id"
        loading={tableQueryResult.isLoading}
        locale={{
          emptyText: (
            <Empty
              description={
                hasActiveFilters
                  ? 'No sandboxes match your filters'
                  : 'No sandboxes found. Create your first sandbox!'
              }
            />
          ),
        }}
      >
        <Table.Column
          dataIndex="id"
          title="Sandbox ID"
          sorter
          width={200}
        />

        <Table.Column
          dataIndex="status"
          title="Status"
          width={120}
          render={(status: SandboxStatus) => <StatusBadge status={status} />}
        />

        <Table.Column
          dataIndex="url"
          title="URL"
          width={350}
          render={(url: string) => <URLDisplay url={url} />}
        />

        <Table.Column
          dataIndex={['spec', 'cluster']}
          title="Cluster"
          width={150}
        />

        <Table.Column
          dataIndex="created_by"
          title="Created By"
          width={200}
        />

        <Table.Column
          dataIndex="created_at"
          title="Created At"
          width={180}
          sorter
          render={(date: string) => new Date(date).toLocaleString()}
        />

        <Table.Column
          title="Actions"
          width={120}
          fixed="right"
          render={(_, record: Sandbox) => (
            <Space>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                href={`/sandboxes/show/${record.id}`}
              >
                View
              </Button>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              >
                Delete
              </Button>
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
